// Admin user search + welcome/bonus credit grants — thin adapters over valoi_engine's
// authenticated /api/v1/admin/* API (see app/routers/admin_api.py). Every endpoint
// here requires the caller's own Bearer JWT to belong to a role === 'admin' User
// (require_admin_user); a non-admin gets a 403 ApiError, same as any other endpoint.
//
// Identity comes from the Bearer JWT via apiClient, same as every other real call —
// granted_by_user_id/granted_by_name are stamped server-side from the verified admin
// session, never sent from here.

import { deleteJson, getJson, patchJson, postJson } from '@/shared/api/apiClient';
import type {
  AdminFeaturedVideo,
  AdminFeedbackPage,
  AdminUser,
  AdminUserPage,
  CreditGrant,
  CreditGrantPage,
  AdminVideoJobPage,
} from '@/shared/types';

const ADMIN_BASE = '/api/v1/admin';

// Collapses {key: value, ...} into a leading-`?` query string, dropping any key
// whose value is null/undefined/empty-string — every admin endpoint here takes an
// all-optional filter/pagination bag, so each call site would otherwise repeat this
// same "if set, query.set(...)" line once per param.
function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') query.set(key, String(value));
  }
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

// --- Wire shapes (local — only cross-file types live in types.ts) ------------

type AdminUserApiItem = {
  id: string;
  email: string;
  name: string | null;
  phone_number: string | null;
  role: string;
  created_at: string;
  credits: number | null;
};

type AdminUserListApiResponse = {
  total: number;
  limit: number;
  offset: number;
  users: AdminUserApiItem[];
};

type CreditGrantApiItem = {
  id: string;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  amount_credits: number;
  label: string;
  reason: string | null;
  granted_by_user_id: string;
  granted_by_name: string;
  created_at: string;
};

type CreditGrantListApiResponse = {
  total: number;
  limit: number;
  offset: number;
  grants: CreditGrantApiItem[];
};

type AdminFeedbackApiItem = {
  id: string;
  user_id: string;
  name: string | null;
  email: string;
  message: string;
  job_id: string | null;
  created_at: string;
};

type AdminFeedbackListApiResponse = {
  total: number;
  limit: number;
  offset: number;
  items: AdminFeedbackApiItem[];
};

// role is validated against this set rather than blindly cast — an unrecognized
// value (e.g. a future role this build predates) degrades to 'user' rather than
// silently granting admin UI on a bad assumption. See adminService's own
// `role !== 'admin'` gate usages throughout the admin pages.
const ADMIN_ROLES: readonly string[] = ['user', 'admin'];

function toAdminUser(item: AdminUserApiItem): AdminUser {
  return {
    id: item.id,
    email: item.email,
    name: item.name,
    phoneNumber: item.phone_number,
    role: ADMIN_ROLES.includes(item.role) ? (item.role as 'user' | 'admin') : 'user',
    createdAt: item.created_at,
    credits: item.credits,
  };
}

function toCreditGrant(item: CreditGrantApiItem): CreditGrant {
  return {
    id: item.id,
    userId: item.user_id,
    userEmail: item.user_email,
    userName: item.user_name,
    amountCredits: item.amount_credits,
    label: item.label,
    reason: item.reason,
    grantedByUserId: item.granted_by_user_id,
    grantedByName: item.granted_by_name,
    createdAt: item.created_at,
  };
}

// GET /api/v1/admin/users?search=&start=&end=&limit=&offset= — an empty search
// paginates every signed-up user, newest-first; start/end filter on signup date.
export async function searchUsers(params?: {
  search?: string;
  start?: string;
  end?: string;
  limit?: number;
  offset?: number;
}): Promise<AdminUserPage> {
  const qs = buildQuery({
    search: params?.search,
    start: params?.start,
    end: params?.end,
    limit: params?.limit,
    offset: params?.offset,
  });
  const body = await getJson<AdminUserListApiResponse>(`${ADMIN_BASE}/users${qs}`);
  return {
    total: body.total,
    limit: body.limit,
    offset: body.offset,
    users: body.users.map(toAdminUser),
  };
}

// GET /api/v1/admin/users/{id} — one user's identity, to confirm who a grant is
// about to go to before submitting, or to prefill the edit-user modal.
export async function getUser(userId: string): Promise<AdminUser> {
  const body = await getJson<AdminUserApiItem>(`${ADMIN_BASE}/users/${userId}`);
  return toAdminUser(body);
}

// POST /api/v1/admin/users {email, name?, phone_number?} -> the existing or
// newly-created user. Idempotent by email — for pre-creating an account for
// someone who hasn't signed up yet (e.g. an investor); they merge into this same
// row automatically the first time they sign in with Google. No password: this
// app only ever authenticates via Google OAuth.
export async function createUser(params: {
  email: string;
  name?: string;
  phoneNumber?: string;
}): Promise<AdminUser> {
  const body = await postJson<AdminUserApiItem>(`${ADMIN_BASE}/users`, {
    email: params.email,
    name: params.name || null,
    phone_number: params.phoneNumber || null,
  });
  return toAdminUser(body);
}

// PATCH /api/v1/admin/users/{id} — edits an existing user's profile. Only the
// fields provided here are changed; omit a field to leave it untouched.
export async function updateUser(
  userId: string,
  params: { email?: string; name?: string; phoneNumber?: string; role?: 'user' | 'admin' },
): Promise<AdminUser> {
  const body = await patchJson<AdminUserApiItem>(`${ADMIN_BASE}/users/${userId}`, {
    email: params.email,
    name: params.name,
    phone_number: params.phoneNumber,
    role: params.role,
  });
  return toAdminUser(body);
}

// POST /api/v1/admin/users/{id}/credit-grants — grants welcome/bonus credit.
// `amountCredits` is credits (1 credit = $1), not cents. `label` is user-facing
// (shows on their wallet activity feed); `reason` is internal-only.
export async function grantCredit(
  userId: string,
  params: { amountCredits: number; label: string; reason?: string; idempotencyKey?: string },
): Promise<CreditGrant> {
  const body = await postJson<CreditGrantApiItem>(`${ADMIN_BASE}/users/${userId}/credit-grants`, {
    amount_credits: params.amountCredits,
    label: params.label,
    reason: params.reason || null,
    idempotency_key: params.idempotencyKey,
  });
  return toCreditGrant(body);
}

// GET /api/v1/admin/credit-grants — filterable, paginated audit log of every
// admin-initiated credit grant. `search` matches the target user's email/name.
export async function listCreditGrants(params?: {
  search?: string;
  grantedBy?: string;
  start?: string;
  end?: string;
  limit?: number;
  offset?: number;
}): Promise<CreditGrantPage> {
  const qs = buildQuery({
    search: params?.search,
    granted_by: params?.grantedBy,
    start: params?.start,
    end: params?.end,
    limit: params?.limit,
    offset: params?.offset,
  });
  const body = await getJson<CreditGrantListApiResponse>(`${ADMIN_BASE}/credit-grants${qs}`);
  return {
    total: body.total,
    limit: body.limit,
    offset: body.offset,
    grants: body.grants.map(toCreditGrant),
  };
}

// GET /api/v1/admin/feedback — every feedback submission, newest first.
export async function listFeedback(params?: {
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<AdminFeedbackPage> {
  const qs = buildQuery({
    search: params?.search,
    limit: params?.limit,
    offset: params?.offset,
  });
  const body = await getJson<AdminFeedbackListApiResponse>(`${ADMIN_BASE}/feedback${qs}`);
  return {
    total: body.total,
    limit: body.limit,
    offset: body.offset,
    items: body.items.map((item) => ({
      id: item.id,
      userId: item.user_id,
      name: item.name,
      email: item.email,
      message: item.message,
      jobId: item.job_id,
      createdAt: item.created_at,
    })),
  };
}

export type AdminVideoJobApiItem = {
  job_id: string;
  title: string | null;
  status: string;
  mode: string;
  created_at: string;
  total_cost_usd: number;
  runtime_seconds: number | null;
  provider_seconds: number;
  events: number;
};

export type AdminVideoJobListApiResponse = {
  total: number;
  limit: number;
  offset: number;
  items: AdminVideoJobApiItem[];
};

export async function listVideoJobs(params?: {
  userId?: string;
  jobId?: string;
  status?: string;
  minCost?: number;
  maxCost?: number;
  limit?: number;
  offset?: number;
}): Promise<AdminVideoJobPage> {
  const qs = buildQuery({
    user_id: params?.userId,
    job_id: params?.jobId,
    status: params?.status,
    min_cost: params?.minCost,
    max_cost: params?.maxCost,
    limit: params?.limit,
    offset: params?.offset,
  });
  const body = await getJson<AdminVideoJobListApiResponse>(`${ADMIN_BASE}/videos${qs}`);
  return {
    total: body.total,
    limit: body.limit,
    offset: body.offset,
    items: body.items.map((item) => ({
      jobId: item.job_id,
      title: item.title,
      status: item.status,
      mode: item.mode,
      createdAt: item.created_at,
      totalCostUsd: item.total_cost_usd,
      runtimeSeconds: item.runtime_seconds,
      providerSeconds: item.provider_seconds,
      events: item.events,
    })),
  };
}

export async function revertVideoCredits(videoId: string): Promise<{ status: string; message: string }> {
  return await postJson<{ status: string; message: string }>(`${ADMIN_BASE}/videos/${videoId}/revert`, {});
}

// --- Featured Home entries (GET/POST/PATCH/DELETE /api/v1/admin/featured) -------
//
// The curation surface for the Home page. Two things about it are load-bearing:
//
// 1. **Edits go through PATCH, never delete-and-recreate.** `id` here is the
//    curation row's id, which the browse feed reports as the video's id to every
//    user who does not own the underlying job — and `saved_content.content_id`,
//    `shelf_items.item_id`, `notes.video_id` and `watch_progress.video_id` all
//    key on it with no foreign key anywhere. Recreating a row mints a new UUID
//    and orphans every user's save, note, favourite and resume point for that
//    video; the client's re-point pass matches on that id, so it cannot recover
//    them either.
// 2. **`undefined` and `null` mean different things on the way out.** The engine
//    reads `model_dump(exclude_unset=True)`, and `JSON.stringify` drops an
//    `undefined` value while keeping a `null` — so omitting a field leaves it
//    alone and passing `null` clears it. Do not "tidy" a `?? null` into these.

type AdminFeaturedApiItem = {
  id: string;
  job_id: string;
  title: string;
  description: string;
  video_url: string;
  thumbnail_url?: string | null;
  vtt_url?: string | null;
  runtime_seconds?: number | null;
  tags?: string[] | null;
  category?: string | null;
  position?: number;
  published_at: string;
  job_status?: string | null;
  /* Optional on the wire so a client built against an older engine still parses;
     `?? false` in the mapper makes "absent" mean "not published", which is the
     safe reading of a missing opt-in. */
  showcase?: boolean;
};

function toAdminFeatured(item: AdminFeaturedApiItem): AdminFeaturedVideo {
  return {
    id: item.id,
    jobId: item.job_id,
    title: item.title,
    description: item.description,
    // Not run through resolveApiUrl: this list is for identifying and editing
    // rows, and the admin endpoint deliberately returns "" for an entry whose
    // job can no longer mint one — which is the signal a curator is here to act
    // on, not a URL to play.
    videoUrl: item.video_url,
    thumbnailUrl: item.thumbnail_url ?? undefined,
    durationSeconds: item.runtime_seconds ?? undefined,
    tags: item.tags ?? [],
    category: item.category ?? null,
    position: item.position ?? 0,
    showcase: item.showcase ?? false,
    publishedAt: item.published_at,
    jobStatus: item.job_status ?? null,
  };
}

// GET /api/v1/admin/featured — every curated entry, including ones the public
// Home list hides because their job has since failed or lost its video.
export async function listFeaturedAdmin(): Promise<AdminFeaturedVideo[]> {
  const body = await getJson<AdminFeaturedApiItem[]>(`${ADMIN_BASE}/featured`);
  return body.map(toAdminFeatured);
}

// POST /api/v1/admin/featured — feature an existing completed job.
export async function createFeatured(params: {
  jobId: string;
  title?: string;
  description?: string;
  tags?: string[];
  category?: string | null;
  position?: number;
}): Promise<AdminFeaturedVideo> {
  const body = await postJson<AdminFeaturedApiItem>(`${ADMIN_BASE}/featured`, {
    job_id: params.jobId,
    title: params.title || null,
    description: params.description || null,
    tags: params.tags,
    category: params.category ?? null,
    position: params.position ?? 0,
  });
  return toAdminFeatured(body);
}

// PATCH /api/v1/admin/featured/{id} — edit in place, keeping the row's id.
// Omitted fields are left untouched; an explicit `null` category clears it.
export async function updateFeatured(
  featuredId: string,
  params: {
    /* `null` clears the override so the job's own generated title is used again
       — the same thing an omitted `title` means on create. `undefined` is
       "leave it alone"; the two are not interchangeable here. */
    title?: string | null;
    description?: string | null;
    tags?: string[];
    category?: string | null;
    position?: number;
    /* The public-landing-page opt-in. No `| null`: the column is NOT NULL, and the
       engine's validator rejects an explicit null the same way it does for
       `position`. Omit it to leave the flag alone. */
    showcase?: boolean;
  },
): Promise<AdminFeaturedVideo> {
  const body = await patchJson<AdminFeaturedApiItem>(`${ADMIN_BASE}/featured/${featuredId}`, {
    title: params.title,
    description: params.description,
    tags: params.tags,
    category: params.category,
    position: params.position,
    showcase: params.showcase,
  });
  return toAdminFeatured(body);
}

// DELETE /api/v1/admin/featured/{id} — remove an entry from Home entirely.
// Not the way to re-classify one; see the note at the top of this block.
export async function deleteFeatured(featuredId: string): Promise<void> {
  await deleteJson<unknown>(`${ADMIN_BASE}/featured/${featuredId}`);
}
